import React, { useMemo, useState } from "react"
import clsx from "clsx"
import { EyeOutlined, RightOutlined } from "@ant-design/icons"
import type { ReviewKind, ReviewListItem } from "../types"

const KIND_STYLE: Record<ReviewKind, string> = {
    signed: "bg-red-50 text-red-600 border-red-200",
    unsigned: "bg-blue-50 text-blue-600 border-blue-200",
}

const SUBMISSION_STATUS_LABEL: Record<ReviewListItem["submissionStatus"], string> = {
    none: "未保存",
    draft: "暂存",
    submitted: "已提交",
}

const SUBMISSION_STATUS_STYLE: Record<ReviewListItem["submissionStatus"], string> = {
    none: "bg-gray-50 text-gray-500 border-gray-200",
    draft: "bg-amber-50 text-amber-600 border-amber-200",
    submitted: "bg-green-50 text-green-600 border-green-200",
}

const FILTERS: { value: "all" | ReviewKind; label: string }[] = [
    { value: "all", label: "全部" },
    { value: "signed", label: "已签约" },
    { value: "unsigned", label: "未签约" },
]

const ReviewList: React.FC<{
    list: ReviewListItem[]
    onSelect: (id: string) => void
}> = ({ list, onSelect }) => {
    const [filter, setFilter] = useState<"all" | ReviewKind>("all")

    // 「待复盘」统计仅计未提交记录（submissionStatus !== "submitted"）；已提交记录仍保留在列表中可「查看」
    const stats = useMemo(
        () => ({
            signed: list.filter((v) => v.kind === "signed" && v.submissionStatus !== "submitted").length,
            unsigned: list.filter((v) => v.kind === "unsigned" && v.submissionStatus !== "submitted")
                .length,
        }),
        [list]
    )

    const dataSource = useMemo(
        () => (filter === "all" ? list : list.filter((v) => v.kind === filter)),
        [list, filter]
    )

    return (
        <div className="min-h-full">
            <div className="px-6 pt-6 pb-4">
                <h1 className="text-xl font-bold text-gray-800">项目毛利复盘</h1>
                <p className="text-sm text-gray-400 mt-1">
                    针对毛利异常、人力投入异常的项目进行原因复盘与改进跟踪
                </p>

                <div className="mt-5 grid grid-cols-2 gap-4 max-w-xl">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-5 py-4">
                        <div className="text-xs text-gray-400">待复盘 · 已签约项目</div>
                        <div className="mt-1 text-2xl font-semibold text-red-500">
                            {stats.signed}
                            <span className="text-sm font-normal text-gray-400 ml-1">个</span>
                        </div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm px-5 py-4">
                        <div className="text-xs text-gray-400">待复盘 · 未签约项目</div>
                        <div className="mt-1 text-2xl font-semibold text-blue-500">
                            {stats.unsigned}
                            <span className="text-sm font-normal text-gray-400 ml-1">个</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 pb-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-1 px-4 py-3 border-b border-gray-100">
                        {FILTERS.map((f) => (
                            <button
                                key={f.value}
                                onClick={() => setFilter(f.value)}
                                className={clsx(
                                    "px-4 py-1.5 rounded-full text-sm transition-colors",
                                    filter === f.value
                                        ? "bg-dark-bg text-white"
                                        : "text-gray-500 hover:bg-gray-100"
                                )}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[860px] text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-400 bg-gray-50/80">
                                    <th className="font-normal px-5 py-3">项目名称</th>
                                    <th className="font-normal px-4 py-3">项目编号</th>
                                    <th className="font-normal px-4 py-3">复盘类型</th>
                                    <th className="font-normal px-4 py-3">所属所 / 管理所长</th>
                                    <th className="font-normal px-4 py-3">复盘发送月份</th>
                                    <th className="font-normal px-4 py-3">状态</th>
                                    <th className="font-normal px-4 py-3 w-24"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {dataSource.map((item) => (
                                    <tr
                                        key={item.id}
                                        onClick={() => onSelect(item.id)}
                                        className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <td className="px-5 py-3.5 text-gray-800 font-medium">{item.projectName}</td>
                                        <td className="px-4 py-3.5 text-gray-500">{item.projectNo}</td>
                                        <td className="px-4 py-3.5">
                                            <span
                                                className={clsx(
                                                    "inline-block rounded border px-2 py-0.5 text-xs",
                                                    KIND_STYLE[item.kind]
                                                )}
                                            >
                                                {item.kind === "signed" ? "已签约" : "未签约"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-gray-500">
                                            {item.department} / {item.manager}
                                        </td>
                                        <td className="px-4 py-3.5 text-gray-500">{item.reviewMonth}</td>
                                        <td className="px-4 py-3.5">
                                            <span
                                                className={clsx(
                                                    "inline-block rounded border px-2 py-0.5 text-xs",
                                                    SUBMISSION_STATUS_STYLE[item.submissionStatus]
                                                )}
                                            >
                                                {SUBMISSION_STATUS_LABEL[item.submissionStatus]}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5 text-right">
                                            {item.submissionStatus === "submitted" ? (
                                                <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium">
                                                    查看 <EyeOutlined className="text-[11px]" />
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-sm text-dark-bg font-medium">
                                                    去复盘 <RightOutlined className="text-[10px]" />
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {dataSource.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-5 py-16 text-center text-gray-400">
                                            暂无待复盘项目
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ReviewList
